/**
 * AppStoreBilling.ts
 *
 * TypeScript interface + Capacitor plugin registration for the native
 * AppStoreBillingPlugin (StoreKit 2, iOS 15+).
 *
 * Mirrors the GooglePlayBilling plugin shape so BillingManager / providers
 * can treat both stores uniformly.
 */

import { registerPlugin } from '@capacitor/core';

// ─────────────────────────────────────────────
// Shared types
// ─────────────────────────────────────────────

export interface AppStoreProduct {
  productId: string;
  title: string;
  description: string;
  productType: string; // 'subs'
  price: string;            // localised display string, e.g. "$9.99"
  priceValue: number;       // raw numeric value
  currency: string;         // ISO 4217
  billingPeriod?: string;   // e.g. "1M"
}

export interface AppStorePurchase {
  transactionId: string;
  productId: string;
  purchaseDate: string;        // ISO 8601
  expiresDate?: string;        // ISO 8601
  isActive?: boolean;
  /** Signed JWS representation for server-side verification */
  jwsRepresentation: string;
}

export interface AppStorePurchaseResult {
  success: boolean;
  transactionId?: string;
  productId?: string;
  purchaseDate?: string;
  originalTransactionId?: string;
  /** Signed JWS representation — send this to your server to verify */
  jwsRepresentation?: string;
  errorCode?: string;
  errorMessage?: string;
}

export interface PurchaseUpdateEvent {
  type: 'purchase_updated' | 'purchase_error';
  transactionId?: string;
  productId?: string;
  purchaseDate?: string;
  jwsRepresentation?: string;
  error?: string;
}

// ─────────────────────────────────────────────
// Plugin interface
// ─────────────────────────────────────────────

export interface AppStoreBillingPlugin {
  /** Check that StoreKit is available (always resolves on iOS 15+) */
  initConnection(): Promise<{ success: boolean }>;

  /**
   * Fetch product details from the App Store.
   * @param options.skus - Array of App Store product IDs
   */
  getSubscriptions(options: { skus: string[] }): Promise<{ products: AppStoreProduct[] }>;

  /**
   * Initiate a subscription purchase.
   * @param options.productId      - App Store product ID
   * @param options.appAccountToken - UUID v4 that ties the purchase to a BlissNet user (non-PII)
   */
  requestSubscription(options: {
    productId: string;
    appAccountToken?: string;
  }): Promise<AppStorePurchaseResult>;

  /**
   * Return all currently-entitled subscriptions (used for restore).
   */
  getAvailablePurchases(): Promise<{ purchases: AppStorePurchase[] }>;

  /**
   * Mark a StoreKit transaction as finished (call AFTER server verification).
   * @param options.transactionId - The StoreKit transaction ID (UInt64 as string)
   */
  finishTransaction(options: { transactionId: string }): Promise<{ success: boolean }>;

  /** No-op on iOS; mirrors the Android interface. */
  endConnection(): Promise<{ success: boolean }>;

  /**
   * Subscribe to transaction updates (purchases completed outside the app,
   * restored on another device, etc.)
   */
  addListener(
    eventName: 'purchaseUpdate',
    listenerFunc: (event: PurchaseUpdateEvent) => void
  ): Promise<{ remove: () => void }>;

  removeAllListeners(): Promise<void>;
}

// ─────────────────────────────────────────────
// Registration
// ─────────────────────────────────────────────

const AppStoreBilling = registerPlugin<AppStoreBillingPlugin>('AppStoreBilling', {
  // Web / Android stub – StoreKit only available on iOS
  web: () => ({
    initConnection:        async () => { throw new Error('AppStoreBilling is only available on iOS'); },
    getSubscriptions:      async () => { throw new Error('AppStoreBilling is only available on iOS'); },
    requestSubscription:   async () => { throw new Error('AppStoreBilling is only available on iOS'); },
    getAvailablePurchases: async () => { throw new Error('AppStoreBilling is only available on iOS'); },
    finishTransaction:     async () => { throw new Error('AppStoreBilling is only available on iOS'); },
    endConnection:         async () => { throw new Error('AppStoreBilling is only available on iOS'); },
    addListener:           async () => ({ remove: () => {} }),
    removeAllListeners:    async () => {},
  }),
});

export default AppStoreBilling;
